/* eslint-disable react/jsx-filename-extension */
/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Row,
  Col,
  Statistic,
  Pagination,
  message,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  FullscreenExitOutlined,
  FullscreenOutlined,
  PlusOutlined,
  FilterFilled,
  FilterOutlined,
} from '@ant-design/icons';
import { TaskHelpers } from 'ontraccr-common';
import * as Sentry from '@sentry/react';

import TimeCardDatePicker from './TimeCardDatePicker';
import UserStats, { getValueStyle } from './userStats';
import exportExcel from './timecardExcelExport';

import OnTraccrButton from '../common/buttons/OnTraccrButton';

import { calculatePayrollHours } from '../helpers/payroll';
import Analytics from '../helpers/Analytics';

import TimeTracking from '../state/timeTracking';
import {
  getApprovals,
  addPendingApprovalTaskIds,
  removePendingApprovalTaskIds,
} from '../clock/state/clockin.actions';
import {
  EXPORT_TYPE_EXCEL,
  EXPORT_TYPE_PDF,
  timecardPDFExportAdapter,
  buildHoursBreakdownMap,
  getCanEditUnapproved,
} from './timecard.helpers';
import MoreButton from '../common/buttons/MoreButton';
import { getCompanyImageURL } from '../settings/state/settings.actions';
import TimeCardsColumnChanger from './TimeCardsColumnChanger';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import { getIdMap, isNullOrUndefined } from '../helpers/helpers';
import Permissions from '../auth/Permissions';
import BorderlessButton from '../common/buttons/BorderlessButton';
import Debouncer from '../helpers/Debouncer';
import { toggleMenuCollapse } from '../main/state/main.actions';
import { isFilterActive } from '../schedule/GanttSchedule/ganttScheduleHelpers';
import { updateTimeCardFilters } from './state/timecards.actions';
import {
  ITEMS_PER_SUMMARY_PAGE,
  getFormattedListViewTask,
  getCustomFieldMap,
} from './timecardListSummaryView.helpers';
import OnTraccrCheckbox from '../common/inputs/OnTraccrCheckbox';
import OnTraccrSwitch from '../common/inputs/OnTraccrSwitch';
import useEntityOTProfileMap from '../common/hooks/useEntityOTProfileMap';
import useUpdateCostcodeIdMap from '../costcodes/useUpdateCostcodeIdMap';
import { updateCostcodeIdMapForTasks } from '../costcodes/state/costcodes.actions';

const fsDebouncer = new Debouncer();

const PAYROLL_COSTCODE_COLUMNS = [
  'code',
  'name',
  'hourlyWage',
  'dailyWage',
  'wageMultiplier',
  'wageAdjustment',
];

function TimeCardsHeader({
  isApprovals,
  isIndividual,
  hideWeekSelector,
  tasks = [], // Either the payroll tasks on approvals, depending on which mode we are in
  payrollCalculationTasks = [],
  allTasks = [],
  user = {},
  viewType,
  setViewType,
  onAddEntry,
  selectedSummaryRowKeys = [],
  setSelectedSummaryRowKeys,
  isSummary,
  setShowFilterDrawer,
  summaryPage = 1,
  setSummaryPage,
  showOtBreakdown,
  setShowOtBreakdown,
  entryDataMap,
  hoursBreakdown,
  onDateRangeChange,
}) {
  const { t } = useTranslation();
  const { id: userId, wage, position = '' } = user ?? {};

  const isSummaryWithSelected = !!(isSummary && selectedSummaryRowKeys.length);

  const wagePerm = userId === Permissions.id
    ? 'READ_USERS_WAGE_SELF'
    : `READ_USERS_WAGE_${Permissions.formatPosition(position)}`;
  const hasWagePerms = Permissions.has(wagePerm);

  const canAddNew = getCanEditUnapproved(userId, position);

  const dispatch = useDispatch();

  const currentUser = useSelector((state) => state.profile.profile);
  const costcodeIdMap = useSelector((state) => state.costcodes.costcodeIdMap);
  const phases = useSelector((state) => state.costcodes.phases);
  const users = useSelector((state) => state.users.users);
  const isFullScreen = useSelector((state) => state.main.menuCollapsed);
  const timeCardFilters = useSelector((state) => state.timecards.filters);
  const {
    classes = [],
    unions = [],
    locals: unionLocals = [],
  } = useSelector((state) => state.unions);
  const mainLoaded = useSelector((state) => (state.main.loaded));
  const customFields = useSelector((state) => state.timecards.customFields);
  const divisions = useSelector((state) => state.settings.divisions);
  const timeEntryUserMap = useSelector((state) => state.timeTracking.timeEntryUserMap);
  const approvals = useSelector((state) => state.approvals.approvals);
  const pendingApprovalTaskIds = useSelector((state) => state.approvals.pendingApprovalTaskIds);

  const FullScreenIcon = isFullScreen ? FullscreenExitOutlined : FullscreenOutlined;

  const {
    timeRange,
    firstPayrollDay,
    payPeriod,
  } = useSelector((state) => state.timecards);
  const {
    settings,
    companyImageURL,
  } = useSelector((state) => {
    const {
      settings: {
        company: {
          settings: companySettings = {},
          companyImageURL = '',
        } = {},
      } = {},
    } = state;
    return {
      settings: companySettings,
      companyImageURL,
    };
  });

  const {
    enableTimecardSubmission,
  } = settings;

  const [submitLoading, setSubmitLoading] = useState(false);

  const userMap = useMemo(() => getIdMap(users), [users]);
  const phaseMap = useMemo(() => getIdMap(phases), [phases]);
  const classMap = useMemo(() => getIdMap(classes), [classes]);
  const unionMap = useMemo(() => getIdMap(unions), [unions]);
  const unionLocalMap = useMemo(() => getIdMap(unionLocals), [unionLocals]);
  const customFieldMap = useMemo(() => getCustomFieldMap(customFields), [customFields]);

  const userIds = useMemo(() => (userId ? [userId] : []), [userId]);
  const divisionIds = useMemo(() => Object.keys(divisions), [divisions]);

  const exportUserIds = useMemo(() => {
    if (!isSummary) return userIds;
    return [...new Set(allTasks.map((task) => task.userId).filter(Boolean))];
  }, [isSummary, userIds, allTasks]);

  const entityOTProfileMap = useEntityOTProfileMap({
    userIds: isSummary ? exportUserIds : userIds,
    divisionIds,
  });

  const approvalSet = useMemo(() => (
    new Set(approvals.map((approval) => approval.taskId))
  ), [approvals]);

  const isApproveInProgress = useMemo(() => {
    let taskIds = tasks.map((task) => task.id);
    if (isSummaryWithSelected) {
      const summaryRowKeysSet = new Set(selectedSummaryRowKeys);
      taskIds = taskIds.filter((id) => summaryRowKeysSet.has(id));
    }
    return taskIds.some((id) => pendingApprovalTaskIds.has(id));
  }, [tasks, isSummaryWithSelected, selectedSummaryRowKeys, pendingApprovalTaskIds]);

  const payrollTasks = useMemo(() => {
    if (isSummary) return [];
    return payrollCalculationTasks?.length
      ? payrollCalculationTasks
      : tasks;
  }, [isSummary, payrollCalculationTasks, tasks]);

  useUpdateCostcodeIdMap({ items: payrollTasks, columns: PAYROLL_COSTCODE_COLUMNS });

  const payrollHours = useMemo(() => {
    if (isSummary) return {};

    return calculatePayrollHours({
      tasks: payrollTasks,
      companySettings: settings,
      settingsMap: entityOTProfileMap,
      user,
      costcodeMap: costcodeIdMap,
      classMap,
      displayRange: timeRange,
    });
  }, [
    payrollTasks,
    settings,
    user,
    costcodeIdMap,
    classMap,
    isSummary,
    timeRange,
    entityOTProfileMap,
  ]);

  const submittableTasks = useMemo(() => {
    let filteredTasks;

    const { lockPayPeriodSubmissions } = settings;

    if (!lockPayPeriodSubmissions || currentUser.position === 'Admin') {
      filteredTasks = tasks.filter((task) => !task.state
        && !isNullOrUndefined(TaskHelpers.getEndTime(task)));
    } else {
      filteredTasks = tasks.filter((task) => (
        !task.state
        && TaskHelpers.getStartTime(task) >= firstPayrollDay.toMillis()
        && TaskHelpers.getEndTime(task) > 0
      ));
    }

    return filteredTasks;
  }, [
    settings,
    firstPayrollDay,
    payPeriod,
    currentUser,
    tasks,
  ]);

  const isFiltersActive = useMemo(() => {
    const timeCardFiltersObj = timeCardFilters ?? {};
    return isFilterActive(timeCardFiltersObj);
  }, [timeCardFilters]);
  const onSubmit = useCallback(async () => {
    if (!userId || submittableTasks.length === 0 || submitLoading) return;
    setSubmitLoading(true);
    if (await dispatch(TimeTracking.submit({
      tasks: submittableTasks, userId,
    }))) {
      dispatch(getApprovals('On Submit'));
    }
    setSubmitLoading(false);
  }, [dispatch, userId, submittableTasks]);

  const onApproveTasks = useCallback(async () => {
    let approvalTasks = tasks.map((task) => ({
      id: task.id,
      projectId: task.projectId,
      userId: task.userId,
    }));
    if (isSummaryWithSelected) {
      const summaryRowKeysSet = new Set(selectedSummaryRowKeys);
      approvalTasks = approvalTasks.filter((task) => summaryRowKeysSet.has(task.id));
    }
    const taskIds = approvalTasks.map((task) => task.id);
    dispatch(addPendingApprovalTaskIds(taskIds));
    if (await dispatch(TimeTracking.approveTimecards({
      tasks: approvalTasks,
      userId: isSummary ? null : user.id,
      isSummary,
    }))) {
      dispatch(getApprovals('On Mass Approve'));
      dispatch(removePendingApprovalTaskIds(taskIds));
      if (isSummaryWithSelected) setSelectedSummaryRowKeys([]);
    }
  }, [tasks, user, isSummary, selectedSummaryRowKeys, setSelectedSummaryRowKeys]);

  const showApproveModal = useCallback((e) => {
    e.stopPropagation();
    const title = `Approve ${isSummaryWithSelected ? 'Selected' : 'All'} Timecards?`;
    CustomConfirmModal({
      title,
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: async () => {
        await onApproveTasks();
      },
    });
  }, [onApproveTasks, isSummaryWithSelected]);

  const onExport = useCallback((type) => async () => {
    if (!user || !timeRange || timeRange.length !== 2) return;
    const [startDT, endDT] = timeRange;
    const startMillis = startDT.toMillis();
    const endMillis = endDT.toMillis();
    const {
      roundingInterval = 1,
      roundingType,
      roundingSetting,
      showOriginalTimes,
      summaryExportFields,
      enableHourBasedTracking,
    } = settings;
    Analytics.track('Timecards/Export', { ExportFileType: 'xlsx' });

    const nonPendingTasks = allTasks.filter((task) => !task.isPending);
    const exportCostcodeMap = await dispatch(updateCostcodeIdMapForTasks(nonPendingTasks));
    const exportHoursBreakdown = isSummary
      ? buildHoursBreakdownMap({
        userIds: [...new Set(nonPendingTasks.map((task) => task.userId).filter(Boolean))],
        timeEntryUserMap,
        companySettings: settings,
        settingsMap: entityOTProfileMap,
        range: timeRange,
        isApprovals,
        approvalSet,
      })
      : hoursBreakdown;
    const formattedTasks = [];
    const exportDivisionIds = new Set();
    nonPendingTasks.forEach((task) => {
      const formattedTask = getFormattedListViewTask({
        task,
        costCodeMap: exportCostcodeMap,
        phaseMap,
        enableHourBasedTracking,
        unionClassMap: classMap,
        unionLocalMap,
        unionMap,
        entryDataMap,
        userMap,
        hoursBreakdown: exportHoursBreakdown,
      });
      formattedTasks.push(formattedTask);
      exportDivisionIds.add(formattedTask.divisionId);
    });

    let mainColumns;
    let customColumns;
    try {
      if (isSummary) {
        mainColumns = JSON.parse(window.localStorage.getItem('timeCardSummaryColumns'));
      } else if (viewType === 'list') {
        mainColumns = JSON.parse(window.localStorage.getItem('timeCardEntryColumns'));
        const dayColumns = JSON.parse(window.localStorage.getItem('timeCardDayEntryColumns'));
        const dateColumnEnabled = !dayColumns || dayColumns.includes('day');
        if (mainColumns && dateColumnEnabled) {
          mainColumns = mainColumns.concat('day');
        }
      }
      customColumns = JSON.parse(window.localStorage.getItem('timeCardCustomFieldColumns'));
    } catch (err) {
      message.error('Failed to get export column settings');
      Sentry.withScope(() => {
        Sentry.captureException(err);
      });
    }

    if (type === EXPORT_TYPE_PDF) {
      await timecardPDFExportAdapter({
        t,
        companyImageURL,
        tasks: formattedTasks,
        start: startMillis,
        end: endMillis,
        user,
        roundingInterval,
        userMap,
        isSummary,
        roundingType,
        roundingSetting,
        showOriginalTimes,
        summaryExportFields,
        phaseMap,
        mainColumns,
        customColumns,
        customFieldMap,
        divisionIds: exportDivisionIds,
        unionMap,
        unionLocalMap,
        classMap,
        ...payrollHours,
      });
    } else {
      exportExcel({
        t,
        tasks: formattedTasks,
        start: startMillis,
        end: endMillis,
        user: isSummary ? 'Summary' : user.name,
        roundingInterval,
        userMap,
        isSummary,
        roundingType,
        roundingSetting,
        showOriginalTimes,
        mainColumns,
        customColumns,
        unionMap,
        unionLocalMap,
        classMap,
        phaseMap,
        customFieldMap,
        divisionIds: exportDivisionIds,
        ...payrollHours,
      });
    }
  }, [
    allTasks,
    settings,
    timeRange,
    user,
    payrollHours,
    companyImageURL,
    isSummary,
    isApprovals,
    userMap,
    viewType,
    unionMap,
    unionLocalMap,
    classMap,
    phaseMap,
    entryDataMap,
    hoursBreakdown,
    customFieldMap,
    timeEntryUserMap,
    entityOTProfileMap,
    approvalSet,
  ]);

  useEffect(() => {
    dispatch(getCompanyImageURL());
  }, []);

  const onViewTypeChanged = useCallback((checked) => {
    const newViewType = checked ? 'list' : 'card';
    window.localStorage.setItem('timeCardViewType', newViewType);
    setViewType(newViewType);
  }, []);

  const onToggleFullscreen = useCallback(async () => {
    await fsDebouncer.debounce(() => {
      dispatch(toggleMenuCollapse());
    }, 1000);
  }, []);

  const onClearFilter = useCallback(() => {
    dispatch(updateTimeCardFilters(null));
  }, []);

  return (
    <Row justify="start" gutter={16} align="middle" style={{ marginBottom: '0.5rem' }}>
      {(!isApprovals && !hideWeekSelector) && (
        <TimeCardDatePicker
          onDateRangeChange={onDateRangeChange}
          hideAllTime
        />
      )}
      { !isSummary && (
        <Col>
          <Row style={{ flexWrap: 'nowrap' }}>
            <Col style={{ padding: '10px 8px', overflowX: 'auto' }}>
              <UserStats {...payrollHours} />
            </Col>
            { !!(hasWagePerms && wage) && (
              <Col
                className="timecard-stats-container"
                style={{
                  padding: 3,
                  marginTop: 10,
                }}
              >
                <Statistic title="Total Pay" value={payrollHours?.totalPay ?? 0} valueStyle={getValueStyle('green')} />
              </Col>
            )}
          </Row>
        </Col>
      )}
      {!isSummary && (
        <Col>
          <OnTraccrSwitch
            value={viewType === 'list'}
            onChange={onViewTypeChanged}
            style={{ marginLeft: 5 }}
            title={`View ${viewType === 'list' ? 'List ' : 'Card '}`}
          />
        </Col>
      )}
      {!isSummary && viewType !== 'list' && (
      <Col>
        <OnTraccrCheckbox
          label="Show Hours Breakdown"
          value={showOtBreakdown}
          onChange={setShowOtBreakdown}
        />
      </Col>
      )}
      {!isApprovals && canAddNew && (
        <Col>
          <OnTraccrButton
            title="Add"
            icon={<PlusOutlined />}
            onClick={onAddEntry}
            disabled={!mainLoaded}
            loading={!mainLoaded}
          />
        </Col>
      )}
      {!isApprovals && !isSummary && enableTimecardSubmission && (
        <Col>
          <OnTraccrButton
            title="Submit"
            disabled={submitLoading || submittableTasks.length === 0}
            loading={submitLoading}
            onClick={onSubmit}
          />
        </Col>
      )}
      {isApprovals && enableTimecardSubmission && (
        <Col>
          <OnTraccrButton
            title={isSummaryWithSelected ? 'Approve Selected' : 'Approve All'}
            disabled={
              isApproveInProgress
              || tasks.length === 0
              || (!Permissions.has('ENABLE_APPROVE_ALL') && !isSummaryWithSelected)
            }
            loading={isApproveInProgress}
            onClick={(e) => showApproveModal(e)}
          />
        </Col>
      )}
      {isSummary && (
        <>
          <Col>
            <OnTraccrButton
              title=""
              onClick={() => setShowFilterDrawer(true)}
              icon={(
                isFiltersActive
                  ? <FilterFilled style={{ marginLeft: 0 }} />
                  : <FilterOutlined style={{ marginLeft: 0 }} />
              )}
            />
          </Col>
          {isFiltersActive && (
            <Col>
              <OnTraccrButton
                title="Clear Filter"
                onClick={onClearFilter}
              />
            </Col>
          )}
        </>
      )}
      {(viewType === 'list' || isSummary) && (
        <Col>
          <TimeCardsColumnChanger
            isSummary={isSummary}
          />
        </Col>
      )}
      {!isIndividual && (
        <>
          <Col>
            <BorderlessButton
              style={{ width: 30, backgroundColor: 'transparent', padding: 0 }}
              iconNode={<FullScreenIcon style={{ fontSize: 30, marginLeft: 0 }} />}
              onClick={onToggleFullscreen}
            />
          </Col>
          <MoreButton
            options={[
              {
                title: 'Export to Excel',
                icon: 'file-excel',
                style: { padding: 0 },
                onClick: onExport(EXPORT_TYPE_EXCEL),
              },
              {
                title: 'Export to PDF',
                icon: 'file-text',
                style: { padding: 0 },
                onClick: onExport(EXPORT_TYPE_PDF),
              },
            ]}
          />
        </>
      )}
      {isSummary && (
        <Col style={{ marginLeft: 'auto' }}>
          <Pagination
            current={summaryPage}
            hideOnSinglePage
            pageSize={ITEMS_PER_SUMMARY_PAGE}
            total={allTasks?.length ?? 0}
            size="small"
            showSizeChanger={false}
            onChange={(page) => setSummaryPage(page)}
            showLessItems
          />
        </Col>
      )}
    </Row>
  );
}

/* eslint-disable react/forbid-prop-types */
TimeCardsHeader.propTypes = {
  isApprovals: PropTypes.bool,
  isIndividual: PropTypes.bool,
  hideWeekSelector: PropTypes.bool,
  tasks: PropTypes.array,
  payrollCalculationTasks: PropTypes.array,
  allTasks: PropTypes.array,
  user: PropTypes.object,
  viewType: PropTypes.string.isRequired,
  setViewType: PropTypes.func.isRequired,
  onAddEntry: PropTypes.func.isRequired,
  isSummary: PropTypes.bool,
  selectedSummaryRowKeys: PropTypes.array,
  setSelectedSummaryRowKeys: PropTypes.func,
  setShowFilterDrawer: PropTypes.func.isRequired,
  summaryPage: PropTypes.number,
  setSummaryPage: PropTypes.func.isRequired,
  showOtBreakdown: PropTypes.bool,
  setShowOtBreakdown: PropTypes.func,
  entryDataMap: PropTypes.object.isRequired,
  hoursBreakdown: PropTypes.object,
  onDateRangeChange: PropTypes.func,
};

TimeCardsHeader.defaultProps = {
  isApprovals: false,
  isIndividual: false,
  hideWeekSelector: false,
  tasks: [],
  payrollCalculationTasks: [],
  allTasks: [],
  isSummary: false,
  selectedSummaryRowKeys: [],
  setSelectedSummaryRowKeys: () => {},
  user: {},
  summaryPage: 1,
  showOtBreakdown: false,
  setShowOtBreakdown: () => {},
  hoursBreakdown: {},
  onDateRangeChange: null,
};

export default TimeCardsHeader;
